/**
 * Topbar bell — opens dropdown of unresolved user_alerts.
 * Fetches from /api/user-alerts, shows badge with count, lets user
 * dismiss individual alerts or all at once.
 */

import { useEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { useOrgNavigate, useOrgPath } from "@/lib/useOrgNav";
import { alertLocation } from "@/lib/alert-location";
import {
  type UserAlert,
  useDismissAllUserAlerts,
  useDismissUserAlert,
  useUserAlerts,
} from "@/lib/api-hooks";

export function UserAlertsBell() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const q = useUserAlerts({ unresolved: true });
  const dismiss = useDismissUserAlert();
  const dismissAll = useDismissAllUserAlerts();
  const orgNavigate = useOrgNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  useLayer(open, () => setOpen(false));

  const alerts = q.data ?? [];
  const count = alerts.length;

  return (
    <div ref={wrapRef} className="user-alerts-bell" style={{ position: "relative" }}>
      <Btn
        variant="icon"
        title={count > 0 ? `${count} alerts` : "Notifications"}
        onClick={() => setOpen((v) => !v)}
      >
        {I.bell}
      </Btn>
      {count > 0 && (
        <span
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            minWidth: 16,
            height: 16,
            padding: "0 4px",
            background: "var(--c-neg-600)",
            color: "#fff",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 6,
            width: 380,
            maxHeight: 480,
            overflowY: "auto",
            background: "var(--c-surface)",
            border: "1px solid var(--c-border)",
            borderRadius: 10,
            boxShadow: "var(--sh-3)",
            zIndex: 200,
          }}
        >
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--c-divider)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Alerts {count > 0 && `(${count})`}
            </span>
            {count > 0 && (
              <button
                onClick={() => void dismissAll.mutateAsync()}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--c-accent-600)",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Dismiss all
              </button>
            )}
          </div>
          {count === 0 ? (
            <div
              style={{
                padding: 30,
                textAlign: "center",
                color: "var(--c-text-3)",
                fontSize: 13,
              }}
            >
              No alerts.
            </div>
          ) : (
            alerts.map((a) => (
              <AlertRow
                key={a.id}
                alert={a}
                onDismiss={() => void dismiss.mutateAsync(a.id)}
                onGo={(path) => {
                  setOpen(false);
                  orgNavigate(path);
                }}
                onOpen={(() => {
                  const target = linkFor(a.kind, a.payload);
                  if (!target) return undefined;
                  return () => {
                    setOpen(false);
                    void dismiss.mutateAsync(a.id);
                    orgNavigate(target);
                  };
                })()}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Where a tap on the alert takes the user — only kinds with a real
 * destination; the rest stay plain rows with a dismiss ×. ECG kinds open
 * the rule's logs filtered to the keyword with the log row expanded
 * (ECG fixes §4). */
function linkFor(kind: string, payload: Record<string, unknown>): string | null {
  switch (kind) {
    case "preset_suggestion_new":
      return "/autopilot";
    case "ecg_keyword_paused":
    case "ecg_apple_rejected":
    case "ecg_data_missing": {
      const q = new URLSearchParams({ section: "logs" });
      if (payload.rule_id != null) q.set("rule_id", String(payload.rule_id));
      if (payload.keyword_id != null) q.set("entity_id", String(payload.keyword_id));
      if (payload.log_id != null) q.set("log_id", String(payload.log_id));
      return `/rules?${q.toString()}`;
    }
    default:
      return null;
  }
}

function AlertRow({
  alert,
  onDismiss,
  onOpen,
  onGo,
}: {
  alert: UserAlert;
  onDismiss: () => void;
  onOpen?: () => void;
  onGo: (path: string) => void;
}) {
  const title = describeKind(alert.kind, alert.payload);
  const loc = alertLocation(alert.payload);
  const ts = new Date(alert.last_seen_at).toLocaleString();

  return (
    <div
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } } : undefined}
      style={{
        padding: "10px 14px",
        borderBottom: "1px solid var(--c-divider)",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        cursor: onOpen ? "pointer" : undefined,
      }}
    >
      <span style={{ fontSize: 16, lineHeight: "20px" }}>⚠️</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--c-text)" }}>
          {title.line1}
        </div>
        {loc && (
          <div className="alert-loc">
            <span className="alert-loc-seg">
              <span className="alert-loc-label">Campaign:</span>
              <LocName name={loc.campaignName} href={loc.campaignHref} onGo={onGo} />
            </span>
            {loc.adGroupName && (
              <span className="alert-loc-seg">
                <span className="alert-loc-label">› Ad group:</span>
                <LocName name={loc.adGroupName} href={loc.adGroupHref} onGo={onGo} />
              </span>
            )}
          </div>
        )}
        <div
          style={{
            fontSize: 12,
            color: "var(--c-text-3)",
            marginTop: 2,
            wordBreak: "break-word",
          }}
        >
          {title.line2}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--c-text-3)",
            marginTop: 4,
            display: "flex",
            gap: 10,
          }}
        >
          <span>{ts}</span>
          {alert.occurrence_count > 1 && (
            <span>· {alert.occurrence_count} occurrences</span>
          )}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        style={{
          background: "transparent",
          border: 0,
          color: "var(--c-text-3)",
          fontSize: 16,
          cursor: "pointer",
          padding: "0 4px",
        }}
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

/** Имя кампании или группы: ссылка в Ads Manager, если есть куда вести.
 * Клик не закрывает оповещение (это делает тап по строке) — только уводит
 * на страницу; с модификатором открывается новая вкладка, как у ссылки. */
function LocName({
  name,
  href,
  onGo,
}: {
  name: string;
  href: string | null;
  onGo: (path: string) => void;
}) {
  const orgPath = useOrgPath();
  if (!href) {
    return (
      <span className="alert-loc-name" title={name}>
        {name}
      </span>
    );
  }
  return (
    <a
      className="alert-loc-name"
      href={orgPath(href)}
      title={name}
      onClick={(e) => {
        e.stopPropagation();
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onGo(href);
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {name}
    </a>
  );
}

function describeKind(
  kind: string,
  payload: Record<string, unknown>,
): { line1: string; line2: string } {
  switch (kind) {
    case "rule_misconfigured_non_maxconv": {
      const ruleName = String(payload.rule_name ?? `#${payload.rule_id}`);
      const campaignName = String(
        payload.campaign_name ?? `#${payload.campaign_id}`,
      );
      const observed = String(payload.observed_strategy ?? "UNKNOWN");
      return {
        line1: `Rule "${ruleName}" can only run on Max Conversions campaigns`,
        line2: `Campaign "${campaignName}" is ${observed}. Skip until you switch its bidding strategy.`,
      };
    }
    case "notification_channel_failed": {
      const channel = payload.channel === "email" ? "E-mail" : "Telegram";
      const error = payload.error ? String(payload.error) : "repeated delivery failures";
      return {
        line1: `${channel} notifications stopped for a team member`,
        line2: `Three deliveries in a row failed (${error}). They can resume it in Profile → Notification channels: send a test and confirm it arrived.`,
      };
    }
    case "cpp_test_ended_no_winner": {
      const testName = String(payload.test_name ?? `#${payload.test_id}`);
      const leader = payload.leader_name ? String(payload.leader_name) : null;
      // 'keep' vs 'manual' differ only in wording — both applied nothing.
      const tail =
        payload.reason === "manual"
          ? "Nothing was applied — apply a variant by hand on the test page."
          : "The current page was kept — apply a variant by hand on the test page.";
      return {
        line1: `Test "${testName}" stopped without a confirmed winner`,
        line2: leader
          ? `Point-estimate leader: "${leader}", but the data isn't conclusive. ${tail}`
          : tail,
      };
    }
    case "cpp_variant_no_delivery": {
      const testName = String(payload.test_name ?? `#${payload.test_id}`);
      const names = Array.isArray(payload.variant_names)
        ? (payload.variant_names as unknown[]).map(String).join(", ")
        : "a variant";
      return {
        line1: `Test "${testName}": a variant is scheduled but not serving`,
        line2: `${names} had rotation slots this cycle but zero impressions — check the CPP is live in ASA.`,
      };
    }
    case "cpp_test_auto_stopped_paused_ad_group": {
      const testName = String(payload.test_name ?? `#${payload.test_id}`);
      return {
        line1: `Test "${testName}" stopped automatically — its ad group is paused`,
        line2:
          "Two daily checks in a row found the ad group (or its campaign) PAUSED, " +
          "so the test can't collect data. Re-enable the ad group and start a new test to continue.",
      };
    }
    case "preset_suggestion_new": {
      const ruleName = String(payload.rule_name ?? `#${payload.rule_id}`);
      return {
        line1: "New campaign suggestion",
        line2: `"${ruleName}" (Broad to SKAG Extractor) found qualifying search terms — review and approve in Autopilot.`,
      };
    }
    case "ecg_keyword_paused": {
      const ruleName = String(payload.rule_name ?? `#${payload.rule_id}`);
      const text = String(payload.keyword_text ?? `#${payload.keyword_id}`);
      return {
        line1: `${ruleName}: keyword “${text}” paused`,
        line2: String(payload.reason ?? "Paused by the rule."),
      };
    }
    case "ecg_apple_rejected": {
      const ruleName = String(payload.rule_name ?? `#${payload.rule_id}`);
      const text = String(payload.keyword_text ?? `#${payload.keyword_id}`);
      return {
        line1: `${ruleName}: Apple rejected the change for “${text}”`,
        line2: String(payload.reason ?? "No error text from Apple."),
      };
    }
    case "ecg_data_missing": {
      const ruleName = String(payload.rule_name ?? `#${payload.rule_id}`);
      const n = payload.keywords_skipped != null ? String(payload.keywords_skipped) : "some";
      return {
        line1: `${ruleName}: no data for the run, ${n} keywords skipped`,
        line2: String(payload.reason ?? "Data for the run was not collected."),
      };
    }
    case "rule_goal_skipped": {
      const ruleName = String(payload.rule_name ?? `#${payload.rule_id}`);
      const reason = String(payload.skip_reason ?? "");
      const fixes: Record<string, string> = {
        no_app_id_on_rule:
          "Attach the rule to an app's campaigns/ad groups first (Apply To).",
        goal_target_unit_not_configured:
          "Set the goal metric + day in App Settings → Goals and press Apply.",
        goal_rule_not_configured:
          "Configure the goal's bid caps in App Settings → Goals.",
        no_segment_goal_target:
          "Set a target value for this segment's Goal in App Settings → Goals.",
        target_is_zero:
          "The segment's Goal target is 0 — set a real target in App Settings → Goals.",
        no_uplift_coefficients_configured:
          "Upload uplift coefficients for this app in App Settings → Uplift.",
      };
      return {
        line1: `Rule "${ruleName}" matched but its Go-to-Goal action is skipping`,
        line2:
          (fixes[reason] ?? `Reason: ${reason || "unknown"}.`) +
          " Nothing was applied until this is fixed.",
      };
    }
    default:
      return {
        line1: kind,
        line2: JSON.stringify(payload).slice(0, 140),
      };
  }
}
